/* eslint-disable react/require-default-props */
import React, { useState } from 'react';
import { translate } from '@utils/i18n';
import { IssueStatusEnum } from '../../types';
import ManagerNote from './ManagerNote';
import DeviceInfoToggle from './DeviceInfoToggle';
import IssueLayout from './IssueLayout';
import { humanizeInspectableType } from '../../utils/humanizeInspectableType';
import './styles.scss';
export default function RecurringDeficienciesCard({ issue, defaultExpanded, onToggle, onDeviceInfoToggle, onUpdateIssue, onUndoAction, readOnly = false }) {
    var _a, _b, _c, _d, _e, _f, _g;
    const status = issue.status || IssueStatusEnum.Detected;
    const [isHistoryExpanded, setIsHistoryExpanded] = useState(false);
    const handleSuggestCorrection = () => {
        onUpdateIssue(issue.id, status, true);
    };
    const handleDismissIssue = () => {
        onUpdateIssue(issue.id, IssueStatusEnum.DismissedByBackOffice, false);
    };
    const handleNoteChange = (note) => {
        onUpdateIssue(issue.id, status, issue.markedForSuggestion, note);
    };
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString();
    };
    const toggleHistory = () => {
        setIsHistoryExpanded(!isHistoryExpanded);
    };
    const getLatestDeficiency = () => {
        var _a;
        // Find the most recent deficiency (by inspection date)
        if (issue.issueData.occurrences.length === 0) {
            return undefined;
        }
        const sorted = [...issue.issueData.occurrences].sort((a, b) => new Date(b.inspectionDate).getTime() - new Date(a.inspectionDate).getTime());
        return (_a = sorted[0]) === null || _a === void 0 ? void 0 : _a.deficiencyId;
    };
    const formatDateRange = () => {
        const firstDate = new Date(issue.issueData.firstOccurrenceDate);
        const latestDate = new Date(issue.issueData.latestOccurrenceDate);
        const firstMonth = firstDate.toLocaleString('en-US', { month: 'short' });
        const firstYear = firstDate.getFullYear();
        const latestMonth = latestDate.toLocaleString('en-US', { month: 'short' });
        const latestYear = latestDate.getFullYear();
        return `${firstMonth} ${firstYear} → ${latestMonth} ${latestYear}`;
    };
    const getSortedOccurrences = () => {
        // Filter to only show prior occurrences
        // Use priorDeficiencyIds if available (IP-11825 fix for linked deficiencies)
        // Otherwise fall back to inspection ID comparison for backwards compatibility
        const priorOccurrences = issue.issueData.priorDeficiencyIds
            ? issue.issueData.occurrences.filter((occurrence) => issue.issueData.priorDeficiencyIds.includes(occurrence.deficiencyId))
            : issue.inspectionId
                ? issue.issueData.occurrences.filter((occurrence) => occurrence.inspectionId !== issue.inspectionId)
                : issue.issueData.occurrences;
        return priorOccurrences.sort((a, b) => new Date(b.inspectionDate).getTime() - new Date(a.inspectionDate).getTime());
    };
    const sortedOccurrences = getSortedOccurrences();
    return (React.createElement(IssueLayout, { issueType: "recurring_deficiencies", status: status, defaultExpanded: defaultExpanded, onToggle: onToggle, onSuggestCorrection: handleSuggestCorrection, onDismissIssue: handleDismissIssue, onUndoAction: onUndoAction, readOnly: readOnly, statusChanges: issue.statusChanges, markedForSuggestion: issue.markedForSuggestion, hideSuggestCorrection: true },
        React.createElement("div", { className: "recurring-deficiencies" },
            React.createElement("p", { className: "recurring-deficiencies__description" },
                "A deficiency for this device and question has reoccurred in the latest inspection",
                getLatestDeficiency() ? (React.createElement(React.Fragment, null,
                    ' ',
                    "as",
                    ' ',
                    React.createElement("a", { href: `/deficiencies/${(_a = getLatestDeficiency()) !== null && _a !== void 0 ? _a : ''}`, target: "_blank", rel: "noreferrer" },
                        "Deficiency #",
                        getLatestDeficiency()))) : null,
                "."),
            React.createElement("div", { className: "line-separator" }),
            React.createElement("div", { className: "recurring-deficiencies__device-question" },
                React.createElement("div", { className: "recurring-deficiencies__device-name" },
                    React.createElement("strong", null,
                        humanizeInspectableType((_b = issue.inspectable) === null || _b === void 0 ? void 0 : _b.type),
                        ' ',
                        ((_d = (_c = issue.inspectable) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.identifier) ||
                            ((_f = (_e = issue.inspectable) === null || _e === void 0 ? void 0 : _e.data) === null || _f === void 0 ? void 0 : _f.name) ||
                            `#${issue.issueData.inspectableId}`)),
                issue.issueData.questionsInvolved.map((question, idx) => (React.createElement("div", { key: idx, className: "recurring-deficiencies__question-text" }, question)))),
            React.createElement(DeviceInfoToggle, { inspectable: issue.inspectable, onToggle: onDeviceInfoToggle }),
            React.createElement("div", { className: "recurring-deficiencies__history-section" },
                React.createElement("button", { type: "button", className: "recurring-deficiencies__accordion-header", onClick: toggleHistory, "aria-expanded": isHistoryExpanded },
                    React.createElement("div", { className: "recurring-deficiencies__accordion-title-row" },
                        React.createElement("span", { className: "recurring-deficiencies__accordion-title" },
                            "Occurrence History (",
                            sortedOccurrences.length,
                            ")"),
                        React.createElement("span", { className: "recurring-deficiencies__date-range-tag" },
                            "Date Range: ",
                            formatDateRange(),
                            " (",
                            issue.issueData.daysSpan,
                            " Days)")),
                    React.createElement("i", { className: `fa-light fa-angle-${isHistoryExpanded ? 'down' : 'right'}` })),
                isHistoryExpanded && (React.createElement("div", { className: "qmb-table qmb-table--02 qmb-table--x-full" },
                    React.createElement("table", null,
                        React.createElement("thead", null,
                            React.createElement("tr", null,
                                React.createElement("th", null, "Deficiency"),
                                React.createElement("th", null, "Date"),
                                React.createElement("th", null, "Inspection"),
                                React.createElement("th", null, "Resolution Status"))),
                        React.createElement("tbody", null, sortedOccurrences.map((occurrence) => (React.createElement("tr", { key: occurrence.deficiencyId },
                            React.createElement("td", null,
                                React.createElement("a", { href: `/deficiencies/${occurrence.deficiencyId}`, target: "_blank", rel: "noreferrer" },
                                    "#",
                                    occurrence.deficiencyId)),
                            React.createElement("td", null, formatDate(occurrence.inspectionDate)),
                            React.createElement("td", null,
                                React.createElement("a", { href: `/inspections/${occurrence.inspectionId}`, target: "_blank", rel: "noreferrer" },
                                    "#",
                                    occurrence.inspectionId)),
                            React.createElement("td", null, occurrence.isResolved
                                ? occurrence.resolutionStatus ||
                                    translate('resolved', { namespace: 'features.iim_review.issue_card' })
                                : translate('unresolved', { namespace: 'features.iim_review.issue_card' })))))))))),
            issue.markedForSuggestion && (!readOnly || ((_g = issue.managerNote) === null || _g === void 0 ? void 0 : _g.trim())) && (React.createElement("div", { className: "recurring-deficiencies__manager-note" },
                React.createElement(ManagerNote, { note: issue.managerNote || '', onNoteChange: handleNoteChange, disabled: readOnly }))))));
}
